// src/components/common/EmployeeProfileViewWrapper.js
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEmployees } from '../../contexts/EmployeeContext';
import { useAuth } from '../../hooks/useAuth';
import EmployeeProfileView from './EmployeeProfileView';

/**
 * Reusable wrapper for viewing employee profiles across different portals
 * Handles permission-based viewing for Admin, Manager, and Employee portals
 */
const EmployeeProfileViewWrapper = ({ backPath = '/' }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { employees } = useEmployees();

    // Find the employee being viewed
    const employee = employees.find(emp => emp.id === id);

    // Check if viewing own profile
    const isOwnProfile = user?.id === id || user?.email === employee?.email;

    if (!employee) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(backPath)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-900 dark:text-white" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Employee Profile</h1>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                    <p className="text-gray-500 dark:text-gray-400">Employee not found</p>
                    <button
                        onClick={() => navigate(backPath)}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // In the EMPLOYEE portal, a colleague's profile is limited to the public
    // directory card — job details (joining date, employment type, work
    // location, reporting line, etc.) are visible only on one's own profile.
    // Admin and manager portals (different backPath) keep the full view.
    const isEmployeePortal = backPath.startsWith('/employee');
    if (isEmployeePortal && !isOwnProfile) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(backPath)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Go back"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-900 dark:text-white" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {employee.first_name} {employee.last_name}
                    </h1>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div className="flex items-center gap-4">
                        {employee.profile_picture_url ? (
                            <img
                                src={employee.profile_picture_url}
                                alt=""
                                className="w-16 h-16 rounded-full object-cover"
                            />
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold">
                                {(employee.first_name || '?')[0]}
                            </div>
                        )}
                        <div>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                {employee.first_name} {employee.last_name}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {[employee.designation, employee.department_name || employee.department]
                                    .filter(Boolean).join(' • ') || '—'}
                            </p>
                            {employee.email && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{employee.email}</p>
                            )}
                        </div>
                    </div>
                    <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
                        Detailed profile information is private and visible only to the employee and HR.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate(backPath)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    title="Go back"
                >
                    <ArrowLeft className="w-5 h-5 text-gray-900 dark:text-white" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {isOwnProfile ? 'My Profile' : `${employee.first_name} ${employee.last_name}'s Profile`}
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {employee.designation} • {employee.department}
                    </p>
                </div>
            </div>

            <EmployeeProfileView
                profile={employee}
                employees={employees}
                isEditable={false}  // Read-only for non-admin views
                editMode={false}
                editedProfile={{}}
                onEditChange={() => {}}
                onPhotoUpload={() => {}}
                isOwnProfile={isOwnProfile}
                currentUser={user}
            />
        </div>
    );
};

export default EmployeeProfileViewWrapper;
